import React, { useEffect, useState } from "react";
import { Zap, Plus, Lock, Copy, Check, Trash2, MessageCircle, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ConfirmModal } from "@/components/AppDialogs";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";

/**
 * Admin panel inside AdminEventDetail to manage PIN-less last-minute gifts.
 * The deep-link itself is the credential — sent privately via WhatsApp to
 * the named requester. The link expires at H-6 of the event (logistical cut-off).
 */
export default function LastMinuteGiftsAdmin({ event, onChange }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // productId

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/events/${event.id}/last-minute-gifts`);
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (event?.id) refresh();
  }, [event?.id]);

  const removeItem = (productId) => setDeleteConfirm(productId);
  const performDelete = async () => {
    const productId = deleteConfirm;
    setDeleteConfirm(null);
    if (!productId) return;
    await api.delete(`/admin/events/${event.id}/last-minute-gifts/${productId}`);
    refresh();
    onChange?.();
  };

  const inWindow = event.frozen && !event.admin_locked;
  const buttonLabel = inWindow
    ? t("admin.lastMinute.ctaOpen")
    : t("admin.lastMinute.ctaClosed");

  return (
    <div className="mt-12">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-black text-[#2A0E3C] inline-flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#D81B8C]" />
            {t("admin.lastMinute.sectionTitle")}
          </h2>
          <p className="text-sm text-[#5C4664] mt-1 max-w-2xl">
            {t("admin.lastMinute.sectionSub")}
          </p>
        </div>
        <Button
          onClick={() => setOpenForm(true)}
          disabled={!inWindow}
          data-testid="lm-add-btn"
          className="rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white gap-2 disabled:opacity-60"
        >
          {inWindow ? <Plus className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
          {buttonLabel}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-[#5C4664]">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <p data-testid="lm-empty" className="text-sm text-[#5C4664] italic">
          {t("admin.lastMinute.empty")}
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <LastMinuteRow
              key={it.product_id}
              entry={it}
              eventCode={event.code}
              eventTitle={event.title}
              onDelete={() => removeItem(it.product_id)}
            />
          ))}
        </ul>
      )}

      {openForm && (
        <NewLastMinuteForm
          event={event}
          onClose={() => setOpenForm(false)}
          onCreated={() => {
            setOpenForm(false);
            refresh();
            onChange?.();
          }}
        />
      )}
      <ConfirmModal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={performDelete}
        title="Retirer ce cadeau dernière minute ?"
        description={t("admin.lastMinute.confirmDelete")}
        confirmLabel="Retirer"
        destructive
        testId="lm-delete-confirm"
      />
    </div>
  );
}

function statusBadge(entry, t) {
  if (entry.purchased) {
    return { bg: "#E8F6E5", fg: "#206F18", label: t("admin.lastMinute.statusPurchased") };
  }
  if (entry.expired) {
    return { bg: "#F2E2EC", fg: "#5C4664", label: t("admin.lastMinute.statusExpired") };
  }
  return { bg: "#FFE9B0", fg: "#9A6500", label: t("admin.lastMinute.statusPending") };
}

function LastMinuteRow({ entry, eventCode, eventTitle, onDelete }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const frontendBase = window.location.origin;
  const deepLink = `${frontendBase}/event/${eventCode}/lm/${entry.product_id}`;

  const waMessage = t("admin.lastMinute.whatsappTemplate")
    .replace("{requester}", entry.requester_name || "")
    .replace("{product}", entry.product?.name || "")
    .replace("{event}", eventTitle || "")
    .replace("{link}", deepLink);
  const phone = (entry.requester_phone || "").replace(/[^0-9]/g, "");
  const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(waMessage)}`;

  const copy = async () => {
    await navigator.clipboard.writeText(deepLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const badge = statusBadge(entry, t);

  return (
    <li
      data-testid={`lm-row-${entry.product_id}`}
      className="rounded-2xl bg-white border border-[#F2E2EC] p-4 sm:p-5"
    >
      <div className="flex gap-4">
        {entry.product?.image_url && (
          <div
            className="w-16 h-16 rounded-xl bg-center bg-cover shrink-0"
            style={{ backgroundImage: `url(${entry.product.image_url})` }}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="font-display font-black text-[#2A0E3C] truncate">
                {entry.product?.name || "Produit supprimé"}
              </div>
              <div className="text-xs text-[#5C4664] mt-0.5">
                {entry.requester_name} · {entry.requester_phone}
              </div>
            </div>
            <span
              className="text-[10px] font-bold uppercase tracking-widest px-2 h-5 rounded-full inline-flex items-center"
              style={{ backgroundColor: badge.bg, color: badge.fg }}
            >
              {badge.label}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {!entry.purchased && !entry.expired && (
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`lm-wa-${entry.product_id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#3FA535] hover:bg-[#2E7E27] text-white px-3 h-8 text-xs font-semibold transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                {t("admin.lastMinute.sendWhatsapp")}
              </a>
            )}
            {!entry.purchased && !entry.expired && (
              <button
                onClick={copy}
                data-testid={`lm-copy-${entry.product_id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#F2E2EC] hover:border-[#D81B8C] text-[#2A0E3C] px-3 h-8 text-xs font-semibold transition-colors"
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-[#3FA535]" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                {copied ? t("admin.lastMinute.copied") : t("admin.lastMinute.copyLink")}
              </button>
            )}
            {!entry.purchased && (
              <button
                onClick={onDelete}
                data-testid={`lm-delete-${entry.product_id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#F2E2EC] hover:border-[#D81B8C] hover:text-[#D81B8C] text-[#5C4664] px-3 h-8 text-xs font-semibold transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function NewLastMinuteForm({ event, onClose, onCreated }) {
  const { t } = useLanguage();
  const [step, setStep] = useState("pick"); // pick | done
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [requesterName, setRequesterName] = useState("");
  const [requesterPhone, setRequesterPhone] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/public/products")
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));
  }, []);

  const filtered = products.filter(
    (p) =>
      !query ||
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      (p.category || "").toLowerCase().includes(query.toLowerCase()),
  );

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!selected) {
      setError(t("admin.lastMinute.pickProduct"));
      return;
    }
    if (!requesterName.trim() || !requesterPhone.trim()) {
      setError(
        `${t("admin.lastMinute.requesterName")} + ${t("admin.lastMinute.requesterPhone")}`,
      );
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/events/${event.id}/last-minute-gifts`, {
        product_id: selected.id,
        requester_name: requesterName.trim(),
        requester_phone: requesterPhone.trim(),
      });
      setResult(data);
      setStep("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={true}
      onOpenChange={(o) => !o && (step === "done" ? onCreated() : onClose())}
    >
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
        {step === "pick" ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black text-[#2A0E3C]">
                {t("admin.lastMinute.formTitle")}
              </DialogTitle>
              <DialogDescription className="text-[#5C4664]">
                {t("admin.lastMinute.formSub")}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-5 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("admin.lastMinute.requesterName")}</Label>
                  <Input
                    data-testid="lm-form-name"
                    value={requesterName}
                    onChange={(e) => setRequesterName(e.target.value)}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label>{t("admin.lastMinute.requesterPhone")}</Label>
                  <Input
                    data-testid="lm-form-phone"
                    value={requesterPhone}
                    onChange={(e) => setRequesterPhone(e.target.value)}
                    placeholder="+243 …"
                    className="mt-1"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>{t("admin.lastMinute.pickProduct")}</Label>
                <div className="mt-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C4664]" />
                  <Input
                    data-testid="lm-form-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Rechercher un produit…"
                    className="pl-10"
                  />
                </div>
                <div className="mt-3 max-h-64 overflow-y-auto rounded-xl border border-[#F2E2EC]">
                  {filtered.length === 0 ? (
                    <p className="p-4 text-sm text-[#5C4664] text-center">—</p>
                  ) : (
                    <ul className="divide-y divide-[#F7E8F0]">
                      {filtered.map((p) => {
                        const isSel = selected?.id === p.id;
                        return (
                          <li key={p.id}>
                            <button
                              type="button"
                              onClick={() => setSelected(p)}
                              data-testid={`lm-product-${p.id}`}
                              className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                                isSel ? "bg-[#FCE7F3]" : "hover:bg-[#FFF5FA]"
                              }`}
                            >
                              {p.image_url && (
                                <div
                                  className="w-10 h-10 rounded-lg bg-center bg-cover shrink-0"
                                  style={{ backgroundImage: `url(${p.image_url})` }}
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-[#2A0E3C] truncate text-sm">
                                  {p.name}
                                </div>
                                <div className="text-xs text-[#5C4664]">
                                  {p.category} · ${p.price.toFixed(0)}
                                </div>
                              </div>
                              {isSel && <Check className="w-4 h-4 text-[#D81B8C]" />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              {error && (
                <p
                  data-testid="lm-form-error"
                  className="text-sm text-[#D81B8C] font-medium"
                >
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={submitting}
                data-testid="lm-form-submit"
                className="w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] h-12 font-semibold disabled:opacity-60"
              >
                {submitting ? "…" : t("admin.lastMinute.ctaOpen")}
              </Button>
            </form>
          </>
        ) : (
          <LinkSuccessView result={result} event={event} onClose={onCreated} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function LinkSuccessView({ result, event, onClose }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const waMessage = t("admin.lastMinute.whatsappTemplate")
    .replace("{requester}", result.requester_name || "")
    .replace("{product}", result.product?.name || "")
    .replace("{event}", event.title || "")
    .replace("{link}", result.deep_link);
  const phone = (result.requester_phone || "").replace(/[^0-9]/g, "");
  const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(waMessage)}`;

  const copy = async () => {
    await navigator.clipboard.writeText(result.deep_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Format expires_at as readable local date
  const expiresLabel = result.expires_at
    ? new Date(result.expires_at).toLocaleString("fr-FR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <>
      <DialogHeader>
        <div className="w-12 h-12 rounded-2xl bg-[#E8F6E5] text-[#3FA535] flex items-center justify-center mb-1">
          <Check className="w-6 h-6" />
        </div>
        <DialogTitle className="font-display text-2xl font-black text-[#2A0E3C]">
          {t("admin.lastMinute.successTitle")}
        </DialogTitle>
        <DialogDescription className="text-[#5C4664]">
          {t("admin.lastMinute.successSub")}
        </DialogDescription>
      </DialogHeader>

      <div className="mt-2 space-y-4">
        <div>
          <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
            {t("admin.lastMinute.deepLinkLabel")}
          </Label>
          <div className="mt-1 flex items-center gap-2">
            <Input
              data-testid="lm-success-link"
              readOnly
              value={result.deep_link}
              className="font-mono text-xs"
            />
            <Button
              type="button"
              onClick={copy}
              variant="outline"
              data-testid="lm-success-copy"
              className="rounded-full border-[#F2E2EC] gap-2"
            >
              {copied ? (
                <Check className="w-4 h-4 text-[#3FA535]" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
              {copied
                ? t("admin.lastMinute.copied")
                : t("admin.lastMinute.copyLink")}
            </Button>
          </div>
        </div>

        {expiresLabel && (
          <p
            data-testid="lm-success-expiry"
            className="text-xs text-[#5C4664] -mt-2"
          >
            {t("admin.lastMinute.expiresLabel")} <strong>{expiresLabel}</strong>
          </p>
        )}

        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="lm-success-wa"
          className="block w-full text-center rounded-full bg-[#3FA535] hover:bg-[#2E7E27] text-white h-12 font-semibold leading-[3rem]"
        >
          {t("admin.lastMinute.sendWhatsapp")}
        </a>

        <Button
          type="button"
          onClick={onClose}
          variant="outline"
          data-testid="lm-success-close"
          className="w-full rounded-full border-[#F2E2EC] h-11"
        >
          {t("common.close")}
        </Button>
      </div>
    </>
  );
}
